import React from 'react';
import ReactDOM from 'react-dom';
import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';
import { ApolloProvider } from '@apollo/client';
import throttle from 'lodash-es/throttle';

import '../vendor/polyfills/index';

import client from '../common/graphql/apollo';
import AuthManager from '../common/components/AuthManager';
import ErrorBoundary from '../common/components/ErrorBoundary';
import Container from './components/Container';
import crashReporter from '../common/crashReporter';
import {
  loadStateFromLocalStorage,
  saveStateToLocalStorage,
} from '../common/localStorage';
import variables from '../common/variables';
import utils from '../common/utils';

import appReducer from './reducers/appReducer';
import slideReducer from './reducers/slideReducer';
import hintsReducer from './reducers/hintsReducer';
import fullScreenReducer from './reducers/fullScreenReducer';
import mediaRecorderReducer from './reducers/mediaRecorderReducer';
import mediaViewerReducer from './reducers/mediaViewerReducer';
import preloadReducer from './reducers/preloadReducer';
import fontsReducer from './reducers/fontsReducer';
import { transitionsReducer } from '../common/reducers/transitionsReducer';
import fabric from '../common/fabric';
import embedActions from './actions/embedActions';

window.name = utils.getEmbedWindowNameForId(window.presentationId);

const LOCAL_STORAGE_NAME = 'embed';

fabric.Object.NUM_FRACTION_DIGITS = variables.numberPrecision;

const rootReducer = combineReducers({
  appReducer,
  slideReducer,
  fullScreenReducer,
  hintsReducer,
  mediaRecorderReducer,
  mediaViewerReducer,
  transitionsReducer,
  preloadReducer,
  fontsReducer,
});

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const persistedState = loadStateFromLocalStorage(LOCAL_STORAGE_NAME);

const store = createStore(
  rootReducer,
  persistedState,
  composeEnhancers(applyMiddleware(thunk, crashReporter)),
);

store.subscribe(
  throttle(() => {
    saveStateToLocalStorage(LOCAL_STORAGE_NAME, {
      hintsReducer: store.getState().hintsReducer,
      mediaRecorderReducer: store.getState().mediaRecorderReducer,
    });
  }, 1000),
);

window.externalApi = {
  deinitialize: () => {
    store.dispatch(embedActions.deinitialize());
  },
};

const App = () => {
  return (
    <Provider store={store}>
      <ApolloProvider client={client}>
        <ErrorBoundary
          title={variables.errors.crash.title}
          message={variables.errors.crash.message}
        >
          <AuthManager forceAuthentication={false} color="white">
            <Container />
          </AuthManager>
        </ErrorBoundary>
      </ApolloProvider>
    </Provider>
  );
};

ReactDOM.render(<App />, document.getElementById('app'));
